import React, { useEffect, useMemo, useState } from 'react'
import * as Icons from 'react-feather'
import { toast } from 'sonner'
import InputEnter from '../generic/InputEnter'
import { requestAPI } from '../data/api'
import CircularProgress from '@material-ui/core/CircularProgress'

// Generic (multi-tenant) registrable domains — e.g. nsw.gov.au — where each
// subdomain belongs to a different organization. When a domain is listed here,
// determine-domain-service keeps the full hostname when resolving violations
// instead of collapsing everything to the registrable domain. Admin-only,
// global (not per-client).
export default function GenericDomains() {
  const [domains, setDomains] = useState([])
  const [status, setStatus] = useState('loading') // loading | loaded
  const [search, setSearch] = useState('')
  const [input, setInput] = useState('')
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(() => {
    const sorted = domains.slice().sort((a, b) => a.domain.localeCompare(b.domain))
    const q = search.trim().toLowerCase()
    if (!q) return sorted
    return sorted.filter((d) => d.domain.toLowerCase().includes(q))
  }, [domains, search])

  async function load() {
    setStatus('loading')
    const data = await requestAPI('/generic-domains', 'GET')
    setDomains(Array.isArray(data) ? data : [])
    setStatus('loaded')
  }

  const onAdd = async (value) => {
    const domain = (value || '').trim()
    if (!domain || adding) return
    setAdding(true)
    const results = await requestAPI('/generic-domains', 'POST', { domain })
    setAdding(false)
    const result = Array.isArray(results) ? results[0] : null
    if (!result) {
      toast.error('Something went wrong while adding the domain')
      return
    }
    if (result.status === 'added') {
      toast.success(`Added ${result.domain}`)
      setInput('')
      await load()
    } else if (result.status === 'duplicate') {
      toast.warning(`${result.domain} is already a generic domain`)
    } else {
      toast.error(`"${domain}" is not a valid domain`)
    }
  }

  const onDelete = async (domain) => {
    await requestAPI('/generic-domains', 'DELETE', { domain })
    toast.success(`Removed ${domain}`)
    await load()
  }

  return (
    <div id="generic-domains" className="container">
      <header className="gd-header">
        <div className="gd-header-icon">
          <Icons.Globe size={22} />
        </div>
        <div>
          <h1 className="gd-title">Generic domains</h1>
          <p className="gd-subtitle">
            Some domains (like <code>nsw.gov.au</code>) host many different
            organizations, each on its own subdomain. Add them here so violations
            group one level deeper (e.g. <code>fire.nsw.gov.au</code>) instead of
            all landing in one domain.
          </p>
        </div>
      </header>

      <div className="gd-grid">
        {/* ---- Add ---- */}
        <section className="gd-card gd-add">
          <h2 className="gd-card-title">Add a generic domain</h2>
          <div className="gd-add-input">
            <InputEnter
              value={input}
              disabled={adding}
              onChange={(e) => setInput(e.target.value)}
              onEnter={(value) => onAdd(value)}
              placeholder="nsw.gov.au"
            />
          </div>
          <p className="gd-hint">
            Enter a domain, hostname, or URL. Duplicates and invalid entries are
            skipped.
          </p>
        </section>

        {/* ---- List ---- */}
        <section className="gd-card gd-list-card">
          <div className="gd-list-header">
            <div className="gd-search">
              <Icons.Search size={16} className="gd-search-icon" />
              <input
                className="gd-search-input"
                type="text"
                value={search}
                placeholder="Search domains…"
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  className="gd-search-clear"
                  onClick={() => setSearch('')}
                  title="Clear"
                >
                  <Icons.X size={15} />
                </button>
              )}
            </div>
            <span className="gd-count">
              {search
                ? `${filtered.length} of ${domains.length}`
                : `${domains.length} domain${domains.length === 1 ? '' : 's'}`}
            </span>
          </div>

          {status === 'loading' ? (
            <div className="gd-empty">
              <CircularProgress size={26} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="gd-empty">
              <Icons.Inbox size={26} />
              <span>
                {domains.length === 0
                  ? 'No generic domains yet.'
                  : 'No domains match your search.'}
              </span>
            </div>
          ) : (
            <ul className="gd-list">
              {filtered.map((d) => (
                <GenericDomainRow
                  key={d.domain}
                  domain={d.domain}
                  onDelete={onDelete}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}

function GenericDomainRow({ domain, onDelete }) {
  const [confirming, setConfirming] = useState(false)
  const [removing, setRemoving] = useState(false)

  const remove = async () => {
    setRemoving(true)
    await onDelete(domain)
    // row unmounts on success
  }

  return (
    <li className="gd-row">
      <span className="gd-row-domain" title={domain}>
        {domain}
      </span>
      {confirming ? (
        <span className="gd-row-actions">
          <button
            className="gd-icon-btn gd-confirm-yes"
            onClick={remove}
            disabled={removing}
            title="Confirm remove"
          >
            {removing ? <CircularProgress size={14} /> : <Icons.Check size={15} />}
          </button>
          <button
            className="gd-icon-btn gd-confirm-no"
            onClick={() => setConfirming(false)}
            disabled={removing}
            title="Cancel"
          >
            <Icons.X size={15} />
          </button>
        </span>
      ) : (
        <button
          className="gd-icon-btn gd-row-delete"
          onClick={() => setConfirming(true)}
          title="Remove"
        >
          <Icons.Trash2 size={15} />
        </button>
      )}
    </li>
  )
}
